import { useEffect, useRef, useState } from "react"
import { Loader2, Plus, Search, X } from "lucide-react"
import type { AppShell } from "@/App"
import { Input } from "@/components/ui/input"
import { cn } from "@/lib/utils"
import { searchMappingProducts, type CatalogKind, type MappingSearchRow } from "@/lib/api"

/**
 * Pesquisa de produtos do CRM (nome ou SKU) dos Mapeamentos — a mesma no
 * solar, AC e BC. `taken` devolve o motivo por que um resultado não pode ser
 * escolhido (ex.: «neste grupo»); `hint` acrescenta contexto à linha do SKU
 * (ex.: «já em battery»).
 */
export function ProductSearch({
  shell,
  onPick,
  placeholder,
  catalog,
  taken,
  hint,
  autoFocus,
  inline,
  className,
}: {
  shell: AppShell | null
  onPick: (p: MappingSearchRow) => void
  placeholder: string
  /** Pede à pesquisa as ligações do catálogo AC/BC (`catalog_links`). */
  catalog?: CatalogKind
  taken?: (p: MappingSearchRow) => string | null
  hint?: (p: MappingSearchRow) => string | null
  autoFocus?: boolean
  /** Resultados no fluxo (dentro de uma tabela com scroll, o flutuante é cortado). */
  inline?: boolean
  className?: string
}) {
  const [query, setQuery] = useState("")
  const [results, setResults] = useState<MappingSearchRow[]>([])
  const [searching, setSearching] = useState(false)
  const [open, setOpen] = useState(false)
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null)

  useEffect(() => {
    if (!shell || query.trim().length < 2) {
      setResults([])
      return
    }
    if (timer.current) clearTimeout(timer.current)
    timer.current = setTimeout(async () => {
      setSearching(true)
      try {
        setResults(await searchMappingProducts(shell, query.trim(), catalog))
        setOpen(true)
      } catch {
        setResults([])
      } finally {
        setSearching(false)
      }
    }, 300)
    return () => {
      if (timer.current) clearTimeout(timer.current)
    }
  }, [query, shell, catalog])

  return (
    <div className={cn("relative", className)}>
      <div className="flex items-center gap-2 rounded-md border border-border px-3 py-2">
        <Search className="h-4 w-4 text-muted-foreground" />
        <Input
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          onFocus={() => results.length > 0 && setOpen(true)}
          placeholder={placeholder}
          autoFocus={autoFocus}
          autoComplete="off"
          autoCorrect="off"
          spellCheck={false}
          name="mapeamentos-search"
          className="border-0 p-0 shadow-none focus-visible:ring-0"
        />
        {searching && <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />}
        {query && (
          <button onClick={() => { setQuery(""); setOpen(false) }} title="Limpar">
            <X className="h-4 w-4 text-muted-foreground" />
          </button>
        )}
      </div>

      {open && results.length > 0 && (
        <div
          className={cn(
            "mt-1 overflow-auto rounded-md border border-border bg-popover",
            inline ? "max-h-56" : "absolute z-20 max-h-72 w-full min-w-80 shadow-md",
          )}
        >
          {results.map((p) => {
            const blocked = taken?.(p) ?? null
            const extra = hint?.(p) ?? null
            return (
              <button
                key={p.product_id}
                disabled={!!blocked}
                onClick={() => { onPick(p); setQuery(""); setOpen(false) }}
                className={cn(
                  "flex w-full items-center justify-between gap-2 px-3 py-2 text-left text-sm hover:bg-muted",
                  blocked && "cursor-not-allowed opacity-50 hover:bg-transparent",
                )}
              >
                <span className="min-w-0">
                  <span className="block truncate font-medium">{p.alt_name || p.name}</span>
                  <span className="block truncate font-mono text-xs text-muted-foreground">
                    {p.sku || "— sem SKU"}
                    {extra && ` · ${extra}`}
                  </span>
                </span>
                {blocked ? (
                  <span className="shrink-0 text-xs text-muted-foreground">{blocked}</span>
                ) : (
                  <Plus className="h-4 w-4 shrink-0 text-primary" />
                )}
              </button>
            )
          })}
        </div>
      )}
    </div>
  )
}
